import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/auth";
import { Nav, Offcanvas } from "react-bootstrap";
import { IconDefinition, SizeProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendar,
  faGauge,
  faUser,
  faUserTie,
  faListCheck,
  faTrophy,
  faBuilding,
  faRankingStar,
  faTicket,
  faBell,
  faFileInvoiceDollar,
  faBuildingCircleCheck,
  faCircleQuestion,
  faGift,
} from "@fortawesome/free-solid-svg-icons";
import { Roles } from "../enums/Roles";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./AppSidebar.css";
import Logo from "../common/Logo";
import wilsonLogo from "../assets/logo/logo-wilson.png";

export type NavigationLink = {
  key: string;
  icon: IconDefinition;
  title: string;
  url: string;
  hide?: boolean;
  admin?: boolean;
};

export const NAVIGATION_MENU: NavigationLink[] = [
  {
    key: "dashboard",
    icon: faGauge,
    title: "Dashboard",
    url: "/dashboard",
  },
  {
    key: "prenotazioni",
    icon: faCalendar,
    title: "Prenotazioni",
    url: "/prenotazioni",
  },
  {
    key: "utenti",
    icon: faUser,
    title: "Utenti",
    url: "/utenti",
  },
  {
    key: "maestri",
    icon: faUserTie,
    title: "Maestri",
    url: "/maestri",
  },
  {
    key: "facilities",
    icon: faListCheck,
    title: "Gestione Campi",
    url: "/gestione-campi",
  },
  {
    key: "tournaments",
    icon: faTrophy,
    title: "Tornei",
    url: "/tornei",
  },
  {
    key: "memberships",
    icon: faTicket,
    title: "Abbonamenti",
    url: "/abbonamenti",
  },
  {
    key: "rankings",
    icon: faRankingStar,
    title: "Classifica",
    url: "/classifica",
  },
  {
    key: "send-notifications",
    icon: faBell,
    title: "Invia Notifiche",
    url: "/invia-notifiche",
  },
  {
    key: "assign-points",
    icon: faGift,
    title: "Assegna Punti",
    url: "/assegna-punti",
    admin: true,
  },
  {
    key: "quiz",
    icon: faCircleQuestion,
    title: "Quiz",
    url: "/quiz",
    admin: true,
  },
  {
    key: "register-structure",
    icon: faBuildingCircleCheck,
    title: "Registra Struttura",
    url: "/registra-struttura",
    admin: true,
  },
  {
    key: "structures-payments",
    icon: faFileInvoiceDollar,
    title: "Pagamenti Strutture",
    url: "/gestione-pagamenti",
    admin: true,
  },
  {
    key: "structure-data",
    icon: faBuilding,
    title: "Dati della Struttura",
    url: "/dati-struttura",
  },
  {
    key: "billing-info",
    icon: faUserTie,
    title: "Dati di Fatturazione",
    url: "/dati-fatturazione",
  },
];

type Props = {
  show: boolean;
  handleClose: VoidFunction;
};

type LinkProps = {
  link: NavigationLink;
  vertical?: boolean;
  iconSize?: SizeProp;
};

export function SidebarLink({ link, iconSize, vertical }: LinkProps) {
  const location = useLocation();
  const match = location.pathname.startsWith(link.url);

  return (
    <Link
      className={classNames("sidebar-link", { active: match })}
      to={link.url}
    >
      <FontAwesomeIcon
        icon={link.icon}
        className="text-secondary"
        fixedWidth={true}
        size={iconSize}
      />
      <span className={classNames({ "ms-3": !vertical })}>{link.title}</span>
    </Link>
  );
}

export default function AppSidebar({ show, handleClose }: Props) {
  const location = useLocation();
  const { loggedUser, billingData } = useAuth();

  useEffect(
    () => handleClose(),
    // eslint-disable-next-line
    [location.pathname]
  );

  return (
    <Offcanvas show={show} onHide={handleClose}>
      <div className="get-move-sidebar d-flex flex-column h-100">
        <Offcanvas.Header className="border-bottom-primary" closeButton>
          <Offcanvas.Title>
            <FontAwesomeIcon className="text-secondary" icon={faUser} />
            <span className="ms-3">
              {billingData
                ? billingData.name.toUpperCase()
                : `${loggedUser?.name} ${loggedUser?.familyName}`}
            </span>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="sidebar-content flex-grow-1 d-flex flex-column">
          <Nav defaultActiveKey="/" className="flex-column flex-grow-1">
            {NAVIGATION_MENU.filter((link) => !link.hide)
              .filter(
                (link) =>
                  !link.admin || loggedUser?.role === Roles.Superadmin
              )
              .map((link) => (
                <SidebarLink key={link.key} link={link} />
              ))}
          </Nav>

          <Nav className="mt-auto justify-content-center">
            <Logo
              height={75}
              logo={wilsonLogo}
              className="d-inline-block px-md-3"
            />
          </Nav>
        </Offcanvas.Body>
      </div>
    </Offcanvas>
  );
}
